// Types and pure helpers for the Keywords page (pages/Keywords.tsx).
// No imports and only erasable types, so Node tests load it directly
// (test/keywords-ui.test.js), like ads.ts.

export type Competition = "low" | "medium" | "high" | null;
export type Month = { month: string; searches: number | null };
export type KeywordRow = {
  keyword: string; avg_monthly_searches: number | null; monthly: Month[] | null;
  competition: Competition; competition_index: number | null;
  low_bid: number | null; high_bid: number | null; currency: string | null;
  on_list?: boolean;
};
export type SavedRow = KeywordRow & {
  id: number; tag: string; geo_target: string; geo_label: string; source: string;
  metrics_at: string | null; metrics_error: string | null; notes: string | null;
  added_at: string; added_by_name: string | null;
  sc_clicks: number | null; sc_impressions: number | null; sc_position: number | null;
  bl_google: number | null; bl_maps: number | null; bl_volume: number | null;
};
export type Place = { geo: string; geo_label: string; location_id: number | null };
export type ClientLocation = { location_id: number; market: string; city: string; state: string | null; geo: string | null; geo_label: string | null };
export type Source = { id: string; label: string; ok: boolean; reason: string | null };
export type ScSite = { client_id: number; site_url: string; matched_by: "auto" | "admin"; last_pulled_at: string | null; last_status: string | null; last_error: string | null };
export type KeywordStatus = {
  client: { id: number; name: string; website_url: string | null };
  sources: Source[];
  tags: string[];
  locations: ClientLocation[];
  /** False when Google could not be asked to place the cities at all (no Ads connection, quota). */
  locations_checked?: boolean;
  default_location: Place;
  search_console: { scope: boolean; site: ScSite | null };
};

/**
 * What the place row shows on a tab. The place only matters where numbers are
 * fetched or saved: Research once Keyword Planner works, Search Console once a
 * property is connected. "Google couldn't place …" only when Google was asked.
 */
export function placeNotes(s: KeywordStatus, tab: string): { picker: boolean; noCity: boolean; unplaced: string[] } {
  const plannerOk = Boolean(s.sources[0]?.ok);
  const picker = tab === "research" ? plannerOk : tab === "search-console" ? Boolean(s.search_console.scope && s.search_console.site) : false;
  if (!picker) return { picker, noCity: false, unplaced: [] };
  const asked = plannerOk && s.locations_checked !== false;
  return {
    picker,
    noCity: !s.locations.length,
    unplaced: asked ? s.locations.filter((l) => !l.geo).map((l) => `${l.city}${l.state ? `, ${l.state}` : ""}`) : [],
  };
}
export type ScRow = { query: string; clicks: number; impressions: number; ctr: number | null; position: number | null; on_list: boolean };
export type ScResponse = { scope: boolean; days: number; site: ScSite | null; website_url: string | null; rows: ScRow[]; message?: string; error?: string; code?: string };
export type SaveResult = { saved: number; existing: number; skipped: number; metrics: { status: "ok" | "failed"; count: number; error?: string; code?: string } };

export const TAG_LABELS: Record<string, string> = { service: "Service", brand: "Brand", location: "Location", negative: "Negative", other: "Other" };

/** One keyword per line or comma; trimmed, lowercased, blanks and repeats dropped. */
export function keywordsFromText(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[\n,]/)) {
    const k = part.replace(/\s+/g, " ").trim().toLowerCase();
    if (k && !seen.has(k)) { seen.add(k); out.push(k); }
  }
  return out;
}

/** Google Ads keyword limits; the server applies the same ones (utils/keywords.js). */
export const MAX_KEYWORD = 80;
export const MAX_WORDS = 10;
export const MAX_SAVE = 1000;

/** What a save would send: normalized, deduplicated, without keywords Google Ads can't take, at most 1,000. */
export function saveable(list: string[]): { keywords: string[]; skipped: number; over: number } {
  const seen = new Set<string>();
  const ok: string[] = [];
  let skipped = 0;
  for (const raw of list) {
    let k = raw.replace(/\s+/g, " ").trim();
    if ((k.startsWith("[") && k.endsWith("]")) || (k.startsWith('"') && k.endsWith('"'))) k = k.slice(1, -1).trim();
    k = k.toLowerCase();
    if (!k) continue;
    if (k.length > MAX_KEYWORD || k.split(" ").length > MAX_WORDS) { skipped++; continue; }
    if (!seen.has(k)) { seen.add(k); ok.push(k); }
  }
  return { keywords: ok.slice(0, MAX_SAVE), skipped, over: Math.max(0, ok.length - MAX_SAVE) };
}

export const fmtVolume = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("en-US"));

export function fmtBid(value: number | null | undefined, currency: string | null | undefined) {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  } catch {
    return value.toFixed(2);
  }
}

/** "low – high" top-of-page bid, or whichever end Google gave. */
export function bidRange(low: number | null, high: number | null, currency: string | null) {
  if (low == null && high == null) return "—";
  if (low == null || high == null) return fmtBid(low ?? high, currency);
  return `${fmtBid(low, currency)} – ${fmtBid(high, currency)}`;
}

export const fmtPosition = (n: number | null | undefined) => (n == null ? "—" : n.toFixed(1));
export const fmtCtr = (n: number | null | undefined) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);

/** Searches per month, oldest first; a month without a number counts as 0. */
export function trendValues(monthly: Month[] | null | undefined): number[] {
  return [...(monthly || [])].sort((a, b) => a.month.localeCompare(b.month)).map((m) => m.searches ?? 0);
}

export function competitionTone(c: Competition): "danger" | "warning" | "success" | "neutral" {
  return c === "high" ? "danger" : c === "medium" ? "warning" : c === "low" ? "success" : "neutral";
}

/** A sorted copy; rows without a value always go last. */
export function sortRows<T extends Record<string, any>>(rows: T[], key: string, dir: "asc" | "desc"): T[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = a[key], y = b[key];
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * sign;
  });
}

export function filterRows<T extends { keyword: string; avg_monthly_searches: number | null }>(rows: T[], { text, minVolume }: { text: string; minVolume: number }): T[] {
  const needle = text.trim().toLowerCase();
  return rows.filter((r) => (!needle || r.keyword.includes(needle)) && (!minVolume || (r.avg_monthly_searches ?? 0) >= minVolume));
}
